import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createSession, sendChat, checkAIHealth, bookingChat, conciergeChat, managerChat } from '../services/aiService';
import './ChatPage.css';

// ── Simple inline markdown → JSX renderer ────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const result = [];
  let listBuffer = [];
  let key = 0;

  const flushList = () => {
    if (listBuffer.length) {
      result.push(
        <ul key={key++}>
          {listBuffer.map((item, i) => <li key={i}>{parseLine(item)}</li>)}
        </ul>
      );
      listBuffer = [];
    }
  };

  const parseLine = (line) => {
    // **bold**
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      /^\*\*(.+)\*\*$/.test(p)
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : p
    );
  };

  lines.forEach((line) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('• ') || trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(trimmed.slice(2));
    } else {
      flushList();
      if (line.trim() === '') {
        result.push(<br key={key++} />);
      } else if (/^#{1,3} /.test(line)) {
        const level = line.match(/^(#{1,3}) /)[1].length;
        const content = line.replace(/^#{1,3} /, '');
        const Tag = `h${level + 2}`;
        result.push(<Tag key={key++} className="chat-heading">{parseLine(content)}</Tag>);
      } else {
        result.push(<p key={key++}>{parseLine(line)}</p>);
      }
    }
  });
  flushList();
  return result;
}

// ── Agent label & color ───────────────────────────────────────────────────────
const AGENT_META = {
  booking:   { name: 'Innio',  color: '#6366f1', emoji: '🏨' },
  concierge: { name: 'Aria',   color: '#10b981', emoji: '🛎️' },
  manager:   { name: 'NOVA',   color: '#f59e0b', emoji: '⚙️' },
  auto:      { name: 'AI',     color: '#6366f1', emoji: '🤖' },
};

export default function ChatPage() {
  const { currentUser, userProfile } = useAuth();

  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [sessionId, setSessionId]     = useState(null);
  const [agentType, setAgentType]     = useState('auto');
  const [aiOnline, setAiOnline]       = useState(true);
  const [initError, setInitError]     = useState('');
  const [chatStateLoaded, setChatStateLoaded] = useState(false);
  const [hasRestoredChat, setHasRestoredChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const historyRef     = useRef([]); // local history for stateless fallback

  const getChatStorageKey = useCallback(() => {
    const uid = userProfile?.userId ?? userProfile?.user_id ?? userProfile?.id ?? currentUser?.uid;
    if (!uid) return null;
    return `innbotics_chat_${uid}`;
  }, [userProfile, currentUser]);

  // Restore chat state for the current user
  useEffect(() => {
    const storageKey = getChatStorageKey();
    if (!storageKey) {
      setMessages([]);
      setSessionId(null);
      setAgentType('auto');
      historyRef.current = [];
      setHasRestoredChat(false);
      setChatStateLoaded(false);
      return;
    }

    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setMessages([]);
        setSessionId(null);
        setAgentType('auto');
        historyRef.current = [];
        setHasRestoredChat(false);
        setChatStateLoaded(true);
        return;
      }

      const parsed = JSON.parse(raw);
      const restoredMessages = Array.isArray(parsed?.messages)
        ? parsed.messages.map((msg) => ({
            ...msg,
            timestamp: msg?.timestamp ? new Date(msg.timestamp) : new Date(),
          }))
        : [];

      setMessages(restoredMessages);
      setSessionId(parsed?.sessionId || null);
      setAgentType(parsed?.agentType || 'auto');
      historyRef.current = Array.isArray(parsed?.history) ? parsed.history : [];
      setHasRestoredChat(restoredMessages.length > 0);
    } catch (_) {
      localStorage.removeItem(storageKey);
      setMessages([]);
      setSessionId(null);
      setAgentType('auto');
      historyRef.current = [];
      setHasRestoredChat(false);
    } finally {
      setChatStateLoaded(true);
    }
  }, [getChatStorageKey]);

  // Persist chat state for the current user
  useEffect(() => {
    if (!chatStateLoaded) return;
    const storageKey = getChatStorageKey();
    if (!storageKey) return;

    const payload = {
      sessionId,
      agentType,
      messages,
      history: historyRef.current,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(storageKey, JSON.stringify(payload));
  }, [messages, sessionId, agentType, chatStateLoaded, getChatStorageKey]);

  // Scroll to bottom on new messages — defer to after browser paint
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [messages]);

  // Determine initial agent type from user role / check-in status
  const getInitialAgent = useCallback(() => {
    const role = userProfile?.role;
    if (role === 'manager') return 'manager';
    if (role === 'guest' && userProfile?.hasActiveBooking) return 'concierge';
    return 'booking';
  }, [userProfile]);

  // Build session context
  const buildContext = useCallback(() => {
    const ctx = {};

    // User identity — critical for booking/concierge tools
    const uid = userProfile?.userId ?? userProfile?.user_id ?? userProfile?.id;
    if (uid != null)            ctx.user_id    = String(uid);
    const name = userProfile?.fullName ?? userProfile?.full_name;
    if (name)                   ctx.user_name  = name;
    if (name)                   ctx.guest_name = name;   // legacy key kept
    if (userProfile?.email)     ctx.user_email = userProfile.email;
    if (userProfile?.role)      ctx.role       = userProfile.role;

    // Optional hotel / booking context
    if (userProfile?.hotel_id)   ctx.hotel_id   = String(userProfile.hotel_id);
    if (userProfile?.booking_id) ctx.booking_id  = String(userProfile.booking_id);

    return ctx;
  }, [userProfile]);

  // Init: health check + create session
  useEffect(() => {
    if (!chatStateLoaded) return;

    let cancelled = false;

    async function init() {
      const healthy = await checkAIHealth();
      if (cancelled) return;
      if (!healthy) {
        setAiOnline(false);
        setInitError('AI assistant is currently offline. Please try again later.');
        return;
      }

      try {
        if (sessionId) return;

        const agent  = getInitialAgent();
        const ctx    = buildContext();
        let token = null;
        try { token = await currentUser?.getIdToken(); } catch (_) {}

        const sess = await createSession(agent, ctx, token);
        if (cancelled) return;

        setSessionId(sess.session_id);
        setAgentType(sess.agent_type);

        if (!hasRestoredChat) {
          const greeting = agent === 'manager'
            ? `Hello ${userProfile?.fullName || 'Manager'}! I'm **NOVA**, your AI Operations Manager. Ask me about staff, tasks, bookings, or hotel status.`
            : agent === 'concierge'
            ? `Welcome back, ${userProfile?.fullName || 'Guest'}! 🛎️ I'm **Aria**, your in-stay assistant. How can I help you today?`
            : `Hi${userProfile?.fullName ? ` ${userProfile.fullName}` : ''}! I'm **Innio**, your hotel booking assistant. I can help you find and book hotels. Where would you like to stay?`;

          setMessages([{
            id: Date.now(),
            role: 'agent',
            agent: sess.agent_type,
            content: greeting,
            timestamp: new Date(),
          }]);
        }
      } catch (err) {
        // Session creation failed — still allow chat via direct endpoints
        if (!cancelled) {
          const agent = getInitialAgent();
          const greeting = agent === 'manager'
            ? `Hello ${userProfile?.fullName || 'Manager'}! I'm **NOVA**, your AI Operations Manager.`
            : agent === 'concierge'
            ? `Welcome back, ${userProfile?.fullName || 'Guest'}! 🛎️ I'm **Aria**, your in-stay assistant. How can I help you today?`
            : `Hi${userProfile?.fullName ? ` ${userProfile.fullName}` : ''}! I'm **Innio**, your hotel booking assistant. Where would you like to stay?`;
          setAgentType(agent);
          if (!hasRestoredChat) {
            setMessages([{ id: Date.now(), role: 'agent', agent, content: greeting, timestamp: new Date() }]);
          }
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [currentUser, userProfile, getInitialAgent, buildContext, sessionId, hasRestoredChat, chatStateLoaded]);

  // Direct (stateless) chat using local history — always works regardless of sessions
  const directChat = useCallback(async (text, token) => {
    const agent = agentType || getInitialAgent();
    const ctx   = buildContext();
    const hist  = historyRef.current;
    let res;
    if (agent === 'manager') {
      res = await managerChat(text, hist, ctx, token);
    } else if (agent === 'concierge') {
      res = await conciergeChat(text, hist, ctx, token);
    } else {
      res = await bookingChat(text, hist, ctx, token);
    }
    return res;
  }, [agentType, getInitialAgent, buildContext]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { id: Date.now(), role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      let token = null;
      try { token = await currentUser?.getIdToken(); } catch (_) {}

      let res;

      if (sessionId) {
        // Try session-based chat first; on 404 fall back to direct
        try {
          res = await sendChat(sessionId, text, token);
        } catch (err) {
          if (err.status === 404 || err.message?.toLowerCase().includes('not found')) {
            // Session gone (server restarted) — try to recreate it
            try {
              const sess = await createSession(getInitialAgent(), buildContext(), token);
              setSessionId(sess.session_id);
              res = await sendChat(sess.session_id, text, token);
            } catch (_) {
              // Session recreation also failed — fall through to direct chat
              res = await directChat(text, token);
            }
          } else {
            throw err;
          }
        }
      } else {
        // No session — use direct stateless chat
        res = await directChat(text, token);
      }

      // Update local history for stateless fallback continuity
      historyRef.current = [
        ...historyRef.current,
        { role: 'user',      content: text },
        { role: 'assistant', content: res.reply },
      ].slice(-20);

      if (res.agent_used) setAgentType(res.agent_used);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'agent',
        agent: res.agent_used || agentType,
        content: res.reply,
        actions: res.actions || [],
        escalation: res.escalation || null,
        timestamp: new Date(),
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 2,
        role: 'agent',
        agent: 'auto',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const meta = AGENT_META[agentType] || AGENT_META.auto;

  if (initError) {
    return (
      <div className="chat-page">
        <div className="chat-offline">
          <span>🤖</span>
          <p>{initError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-page">
      {/* Header */}
      <div className="chat-header" style={{ borderBottomColor: meta.color }}>
        <div className="chat-header-info">
          <div className="chat-avatar" style={{ background: meta.color }}>
            {meta.emoji}
          </div>
          <div>
            <h2 className="chat-agent-name">{meta.name}</h2>
            <span className="chat-agent-label">
              {agentType === 'booking' ? 'Booking Assistant'
                : agentType === 'concierge' ? 'In-Stay Concierge'
                : agentType === 'manager' ? 'Operations Manager'
                : 'AI Assistant'}
            </span>
          </div>
        </div>
        <div className="chat-status">
          <span className="status-dot" style={{ background: aiOnline ? '#10b981' : '#ef4444' }} />
          {aiOnline ? 'Online' : 'Offline'}
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.map(msg => {
          const msgMeta = AGENT_META[msg.agent] || AGENT_META.auto;
          return (
            <div key={msg.id} className={`chat-bubble-row chat-bubble-row--${msg.role}`}>
              {msg.role === 'agent' && (
                <div className="chat-bubble-avatar" style={{ background: msgMeta.color }}>
                  {msgMeta.emoji}
                </div>
              )}
              <div className={`chat-bubble chat-bubble--${msg.role}`}
                style={msg.role === 'agent' ? { borderLeftColor: msgMeta.color } : {}}>
                {msg.role === 'agent' && (
                  <div className="chat-bubble-meta">
                    <span className="chat-bubble-agent" style={{ color: msgMeta.color }}>{msgMeta.name}</span>
                    <span className="chat-bubble-time">
                      {msg.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                )}
                <div className="chat-bubble-content">
                  {msg.role === 'agent' ? renderMarkdown(msg.content) : msg.content}
                </div>
                {msg.escalation && (
                  <div className="chat-escalation-badge">⚡ Escalated to Manager</div>
                )}
              </div>
              {msg.role === 'user' && (
                <div className="chat-bubble-avatar chat-bubble-avatar--user">
                  {userProfile?.fullName?.[0]?.toUpperCase() || '👤'}
                </div>
              )}
            </div>
          );
        })}

        {loading && (
          <div className="chat-bubble-row chat-bubble-row--agent">
            <div className="chat-bubble-avatar" style={{ background: meta.color }}>{meta.emoji}</div>
            <div className="chat-bubble chat-bubble--agent" style={{ borderLeftColor: meta.color }}>
              <div className="chat-typing">
                <span /><span /><span />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input-bar">
        <input
          ref={inputRef}
          className="chat-input"
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
          placeholder={loading ? 'Waiting for response...' : 'Type your message...'}
          disabled={loading || !sessionId}
        />
        <button
          className="chat-send-btn"
          onClick={handleSend}
          disabled={loading || !input.trim() || !sessionId}
          style={{ background: meta.color }}
        >
          ➤
        </button>
      </div>
    </div>
  );
}
